import { RefCallback, useCallback, useRef } from "react"
import { getNodeEnvValue } from "../env/env"
import useKeyControl from "../keyboard/useKeyControl"

// Enable debug mode to make the print iframe visible
const __debug__ = false && getNodeEnvValue() !== "production"

export function usePrintContent(querySelector?: string) {
  function printContent() {
    let $iframe = document.querySelector("#print") as HTMLIFrameElement | null
    let $content = querySelector
      ? document.querySelector(querySelector)
      : nodeRef.current
    if (!$content) {
      // Content is not rendered for some reason.
      return window.alert("Unable to print this content.")
    }
    if ($iframe) {
      // Clean up previous printing.
      // Ideally we'd do this immediately after print,
      // but to do so w/ cross-browser support is difficult.
      document.body.removeChild($iframe)
    }
    // Build new iframe as print target
    $iframe = document.createElement("iframe") as HTMLIFrameElement
    $iframe.id = "print"

    if (__debug__) {
      // ✅ Make the iframe visible for debugging
      $iframe.style.position = "fixed"
      $iframe.style.top = "10px"
      $iframe.style.left = "10px"
      $iframe.style.width = "90vw"
      $iframe.style.height = "90vh"
      $iframe.style.background = "white"
      $iframe.style.border = "2px solid black"
      $iframe.style.zIndex = "999999999"
      console.log("Debug mode: iframe is visible for inspection.")
    } else {
      // Hide the iframe normally
      $iframe.style.position = "absolute"
      $iframe.style.height = "0"
      $iframe.style.width = "0"
    }
    document.body.appendChild($iframe)

    const contentWindow = $iframe.contentWindow
    if (contentWindow) {
      contentWindow.document.open()
      contentWindow.document.write(`
        <html>
          <head>
            ${
              ""
              // HACK ALERT
              // UI fonts (-apple-system etc) don't print correctly,
              // and for whatever reason @media print isn't working either,
              // so we inject font styles on the fly.
            }
            <style>
              body {
                font-family:'ProximaNova',sans-serif !important;
              }
            </style>
          </head>
          <body>${$content.outerHTML}</body>
        </html>
      `)

      // Insert link and style elements from parent document.
      // Using documentFragment to trigger only one reflow and render.
      const documentFragment = document.createDocumentFragment()
      document
        .querySelectorAll("link")
        .forEach((element) =>
          documentFragment.appendChild(element.cloneNode(true))
        )
      document
        .querySelectorAll("style")
        .forEach((element) =>
          documentFragment.appendChild(element.cloneNode(true))
        )

      const styleSheetsStyle = document.createElement("style")
      // Get all the stylesheet styles for every <style /> tag that has empty textContent.
      styleSheetsStyle.textContent = Array.from(
        document.querySelectorAll("style")
      )
        .filter(
          (style): style is HTMLStyleElement & { sheet: CSSStyleSheet } =>
            !style.textContent && style.sheet !== null
        )
        .map(({ sheet }) =>
          Object.keys(sheet.cssRules)
            .map((key) => sheet.cssRules[key as unknown as number].cssText)
            .join(" ")
        )
        .join(" ")

      // Insert styleSheetsStyle
      documentFragment.appendChild(styleSheetsStyle)

      // Append document fragment with links and styles to the iframe document head
      contentWindow.document.head.appendChild(documentFragment)

      contentWindow.document.close()

      if (!__debug__) {
        // Safari is strict about allowing window.print() only during a user gesture.
        // Delaying print behind iframe.onload / timers can cause Safari to show a
        // confirmation prompt or block printing entirely.
        contentWindow.focus()
        contentWindow.print()

        contentWindow.onafterprint = function () {
          if ($iframe && $iframe.parentNode) $iframe.parentNode.removeChild($iframe)
        }
      }
    } else {
      console.log("Debug mode: iframe is visible for inspection.")
    }
  }

  const nodeRef = useRef<HTMLElement | null>(null)
  const setRef = useCallback<RefCallback<HTMLElement>>((node) => {
    // Update the ref's current value
    nodeRef.current = node
  }, [])

  // Keyboard shortcut to trigger print, overriding default browser print behavior.
  useKeyControl("Meta+P", (e) => {
    printContent()
    // prevent default print behavior
    e.preventDefault()
    return false
  })

  return [setRef, printContent] as const
}
